<template>
  <div class="config-workspace">
    <div class="config-container">
      <header class="config-hero">
        <div>
          <div class="eyebrow">FREE MODEL ROUTER</div>
          <h2>免费模型配置</h2>
          <p>
            选择 SeaClaw 默认唤醒的免费模型。底层服务地址和密钥由系统维护，用户无需配置。
          </p>
        </div>
        <div class="config-summary">
          <div class="summary-item">
            <span class="summary-value">{{ totalModelCount }}</span>
            <span class="summary-label">免费模型</span>
          </div>
          <div class="summary-item">
            <span class="summary-value">{{ primaryModelSelector ? "ON" : "OFF" }}</span>
            <span class="summary-label">Primary</span>
          </div>
        </div>
      </header>

      <div class="vault-container">
        <div class="section-title-box">
          <span class="section-title">可用免费模型</span>
          <el-tag type="success" effect="plain" round>无需 API Key</el-tag>
        </div>

        <div v-if="vaultConfig.vault.length === 0" class="empty-models">
          暂无可用模型，请检查 OpenClaw 模型配置。
        </div>

        <el-collapse v-model="activeProviderNames" class="provider-collapse">
          <el-collapse-item
            v-for="prov in vaultConfig.vault"
            :key="prov._uid"
            :name="prov._uid"
          >
            <template #title>
              <div class="collapse-title-row">
                <div class="provider-name">
                  <el-icon class="mr-2"><Connection /></el-icon>
                  {{ getProviderDisplayName(prov) }}
                </div>
                <div class="provider-actions" @click.stop>
                  <el-tag
                    v-if="prov.provider === vaultConfig.primaryProvider"
                    type="success"
                    size="small"
                    effect="dark"
                    round
                    >🟢 当前主帅所在</el-tag
                  >
                </div>
              </div>
            </template>

            <div class="provider-card-body">
              <div class="provider-note">
                <span>系统已接入免费模型服务，您只需要选择默认使用哪一个模型。</span>
              </div>

              <div class="models-sub-section">
                <div class="sub-title-row">
                  <span>模型列表</span>
                  <span class="model-count">{{ prov.models.length }} 个模型</span>
                </div>

                <div
                  v-if="prov.models.length === 0"
                  class="empty-models"
                  style="padding: 10px"
                >
                  还没添加模型哦~
                </div>

                <div
                  v-for="(model, mIndex) in prov.models"
                  :key="mIndex"
                  class="model-card-row"
                  :class="{ active: isPrimaryModel(prov, model) }"
                >
                  <div class="model-mark">
                    {{ getModelInitial(model) }}
                  </div>
                  <div class="model-card-info">
                    <div class="model-card-title">{{ getModelDisplayName(model) }}</div>
                    <div class="model-card-meta">免费模型 · {{ model.id }}</div>
                  </div>
                  <el-button
                    :type="isPrimaryModel(prov, model) ? 'success' : 'primary'"
                    plain
                    size="small"
                    @click="setPrimaryModel(prov, model)"
                  >
                    {{ isPrimaryModel(prov, model) ? "当前主力" : "设为主力" }}
                  </el-button>
                </div>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>

        <el-card shadow="never" class="config-card mt-5">
          <template #header>
            <div class="card-title">指定全局主力模型</div>
          </template>
          <el-alert
            :title="`当前主力模型：${primaryModelDisplay}`"
            type="warning"
            show-icon
            :closable="false"
            style="margin-bottom: 15px"
          />

          <el-select
            v-model="primaryModelSelector"
            placeholder="请从上方卡片中选择一个模型设为主帅"
            style="width: 100%"
            @change="handlePrimaryModelChange"
          >
            <el-option-group
              v-for="prov in vaultConfig.vault"
              :key="prov._uid"
              :label="getProviderDisplayName(prov)"
            >
              <el-option
                v-for="model in prov.models"
                :key="model.id"
                :label="`${model.name} (${model.id})`"
                :value="`${prov.provider}|${model.id}`"
              >
                <span style="float: left">{{ model.name }}</span>
                <span style="float: right; color: #8492a6; font-size: 13px">{{
                  model.id
                }}</span>
              </el-option>
            </el-option-group>
          </el-select>
        </el-card>

        <div class="submit-action">
          <el-button
            type="primary"
            size="large"
            :loading="isSavingConfig"
            @click="saveRemoteConfig"
            class="save-btn"
          >
            <el-icon class="el-icon--left" v-if="!isSavingConfig"
              ><Upload
            /></el-icon>
            保存模型选择
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from "axios";
import { computed, ref, onMounted } from "vue";
import { Connection, Upload } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";

const API_BASE_URL = "/api";

const isSavingConfig = ref(false);
const vaultConfig = ref({
  primaryProvider: "",
  primaryModel: "",
  vault: [],
});

const activeProviderNames = ref([]);
const primaryModelSelector = ref("");

const totalModelCount = computed(() =>
  vaultConfig.value.vault.reduce((sum, prov) => sum + (prov.models?.length || 0), 0),
);

const primaryModelDisplay = computed(() => {
  if (!vaultConfig.value.primaryModel) return "暂未指定";
  const provider = vaultConfig.value.vault.find(
    (prov) => prov.provider === vaultConfig.value.primaryProvider,
  );
  const model = provider?.models?.find((item) => item.id === vaultConfig.value.primaryModel);
  return model ? getModelDisplayName(model) : vaultConfig.value.primaryModel;
});

const getProviderDisplayName = (prov) => {
  if (!prov?.provider) return "免费模型";
  return prov.provider === "ollama" ? "免费模型" : prov.provider;
};

const getModelDisplayName = (model) => {
  const raw = model?.name || model?.id || "未命名模型";
  const names = {
    gemma4: "Gemma 免费模型",
    "gemma4:31b": "Gemma 31B 免费模型",
    "qwen3.6:35b": "Qwen 35B 免费模型",
  };
  return names[raw] || names[model?.id] || raw;
};

const getModelInitial = (model) => {
  const name = getModelDisplayName(model);
  return name.slice(0, 1).toUpperCase();
};

const isPrimaryModel = (prov, model) =>
  prov.provider === vaultConfig.value.primaryProvider &&
  model.id === vaultConfig.value.primaryModel;

const setPrimaryModel = (prov, model) => {
  vaultConfig.value.primaryProvider = prov.provider;
  vaultConfig.value.primaryModel = model.id;
  primaryModelSelector.value = `${prov.provider}|${model.id}`;
};

const fetchRemoteConfig = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}/config/llm`);
    if (res.data) {
      vaultConfig.value = res.data;
      vaultConfig.value.vault.forEach((p) => {
        p._uid = "prov_" + Math.random().toString(36).substr(2, 9);
      });
      activeProviderNames.value = vaultConfig.value.vault.map((p) => p._uid);

      if (vaultConfig.value.primaryProvider && vaultConfig.value.primaryModel) {
        primaryModelSelector.value = `${vaultConfig.value.primaryProvider}|${vaultConfig.value.primaryModel}`;
      }
    }
  } catch (error) {
    console.error("⚠️ 获取远程配置失败:", error);
  }
};

const handlePrimaryModelChange = (val) => {
  if (val) {
    const parts = val.split("|");
    vaultConfig.value.primaryProvider = parts[0];
    vaultConfig.value.primaryModel = parts[1];
  } else {
    vaultConfig.value.primaryProvider = "";
    vaultConfig.value.primaryModel = "";
  }
};

const saveRemoteConfig = async () => {
  if (vaultConfig.value.vault.length === 0) {
    ElMessage.warning("您连一个名片(服务商)都没留！");
    return;
  }
  for (let prov of vaultConfig.value.vault) {
    if (!prov.provider) {
      ElMessage.warning("提供商名称不能为空！");
      return;
    }
  }
  if (!vaultConfig.value.primaryProvider || !vaultConfig.value.primaryModel) {
    ElMessage.warning("必须在下方指定一个全局主帅！");
    return;
  }

  isSavingConfig.value = true;
  try {
    const payload = {
      primaryProvider: vaultConfig.value.primaryProvider,
      primaryModel: vaultConfig.value.primaryModel,
      vault: vaultConfig.value.vault.map((prov) => ({
        provider: prov.provider,
        baseUrl: prov.baseUrl,
        apiKey: prov.apiKey,
        models: prov.models,
      })),
    };

    await axios.put(`${API_BASE_URL}/config/llm`, payload);
    ElMessage({
      message: "配置已保存！将在下次访问对话界面时生效。",
      type: "success",
      duration: 3000,
    });
  } catch (error) {
    ElMessage.error(error.response?.data?.detail || "配置保存失败");
  } finally {
    isSavingConfig.value = false;
  }
};

onMounted(() => {
  fetchRemoteConfig();
});
</script>

<style scoped>
.config-workspace {
  --panel-border: #dce6f2;
  --ink: #203040;
  --muted: #6d7b8a;
  padding: 36px 48px 56px;
  background:
    radial-gradient(circle at 88% 12%, rgba(64, 158, 255, 0.13), transparent 30%),
    linear-gradient(135deg, #f6f9fc 0%, #edf3f8 100%);
  overflow-y: auto;
  height: 100%;
}
.config-container {
  max-width: 1360px;
  margin: 0 auto;
}
.config-hero {
  display: flex;
  justify-content: space-between;
  gap: 28px;
  margin-bottom: 24px;
  padding: 30px 34px;
  border: 1px solid var(--panel-border);
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(241, 247, 252, 0.86)),
    repeating-linear-gradient(0deg, transparent 0 24px, rgba(32, 48, 64, 0.035) 25px);
  box-shadow: 0 24px 60px rgba(55, 84, 112, 0.12);
}
.eyebrow {
  color: #409eff;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.16em;
  margin-bottom: 10px;
}
.config-hero h2 {
  margin: 0 0 12px;
  font-size: 34px;
  line-height: 1;
  letter-spacing: -0.04em;
  color: var(--ink);
}
.config-hero p {
  margin: 0;
  color: var(--muted);
  font-size: 15px;
  max-width: 680px;
}
.config-summary {
  display: grid;
  grid-template-columns: repeat(2, 140px);
  gap: 14px;
}
.summary-item {
  background: #203040;
  border-radius: 18px;
  color: #fff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}
.summary-value {
  font-size: 30px;
  line-height: 1;
  font-weight: 900;
}
.summary-label {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.72);
}
.vault-container {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid var(--panel-border);
  border-radius: 24px;
  box-shadow: 0 18px 42px rgba(55, 84, 112, 0.1);
}
.section-title-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.section-title {
  font-size: 18px;
  font-weight: bold;
  color: var(--ink);
}
.provider-collapse {
  border: 1px solid var(--panel-border);
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 14px 34px rgba(55, 84, 112, 0.08);
}
:deep(.el-collapse-item__header) {
  height: 60px;
  padding: 0 22px;
  font-size: 15px;
  background-color: #f7fafd;
}
:deep(.el-collapse-item__wrap) {
  border-bottom: none;
}
.collapse-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 15px;
}
.provider-name {
  font-weight: bold;
  color: #303133;
  display: flex;
  align-items: center;
}
.provider-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}
.provider-card-body {
  padding: 24px;
  background: #fff;
}
.provider-note {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f0f8ff, #f8fbff);
  color: var(--muted);
  font-size: 14px;
  border: 1px solid #dcecff;
}
.mt-3 {
  margin-top: 15px;
}
.mt-4 {
  margin-top: 20px;
}
.mt-5 {
  margin-top: 30px;
}
.mr-2 {
  margin-right: 8px;
}
.models-sub-section {
  background: #f7fafd;
  border: 1px dashed #b9cce0;
  border-radius: 16px;
  padding: 18px;
}
.sub-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: var(--ink);
  margin-bottom: 15px;
  font-weight: bold;
}
.model-count {
  color: var(--muted);
  font-weight: 600;
}
.empty-models {
  text-align: center;
  color: #909399;
  padding: 20px 0;
  background: #f4f4f5;
  border-radius: 8px;
  font-size: 14px;
}
.model-card-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  background: #fff;
  border-radius: 16px;
  margin-bottom: 12px;
  border: 1px solid #e4edf7;
  box-shadow: 0 10px 24px rgba(55, 84, 112, 0.06);
  transition: all 0.2s ease;
}
.model-card-row:hover {
  transform: translateY(-2px);
  border-color: #b8cbe0;
  box-shadow: 0 16px 34px rgba(55, 84, 112, 0.12);
}
.model-card-row.active {
  border-color: #67c23a;
  background: linear-gradient(135deg, #f2fbef, #ffffff);
}
.model-mark {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #203040;
  color: #fff;
  font-weight: 900;
  font-size: 20px;
}
.model-card-row.active .model-mark {
  background: linear-gradient(135deg, #67c23a, #409eff);
}
.model-card-info {
  flex: 1;
  min-width: 0;
}
.model-card-title {
  color: var(--ink);
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 4px;
}
.model-card-meta {
  color: var(--muted);
  font-size: 13px;
}
.model-fields {
  display: flex;
  flex: 1;
  gap: 12px;
}
.model-input {
  flex: 1;
}
.config-card {
  border-radius: 18px;
  border: 1px solid var(--panel-border);
  box-shadow: 0 14px 34px rgba(55, 84, 112, 0.08);
}
.card-title {
  font-weight: bold;
  font-size: 16px;
}
.submit-action {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  padding-bottom: 28px;
}
.save-btn {
  width: 240px;
  height: 46px;
  border-radius: 14px;
  font-weight: bold;
}
@media (max-width: 980px) {
  .config-workspace {
    padding: 24px;
  }
  .config-hero {
    flex-direction: column;
  }
  .config-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .model-fields {
    flex-direction: column;
  }
}
</style>
